/**
 * Dodging (PLAN §7, highest priority): if a danger area I can see covers me, path to
 * the nearest safe point. Each telegraph gets one dodgeReliability roll; a failed roll
 * means the bot "didn't notice" and stands in it. Persistent zones are re-rolled every
 * ZONE_REROLL_MS, so a less reliable bot leaves a pool late rather than never.
 */
import type { Command, DeepReadonly, Vec2, WorldShape } from '@keystone/sim';
import { angleDiff, findSafePoint, shapeContains } from '../geometry';
import { reactionRoll, rollFor, type BotMemory } from '../memory';
import { simWorld, type DangerArea, type Perception } from '../perception';
import { safeSoakIds } from './mechanics';

/** +encounters: moving hazards are also avoided where they will be this far ahead. */
export const MOVING_LOOKAHEAD_MS = 800;

/** Re-roll window for persistent zones. */
export const ZONE_REROLL_MS = 1000;
/** A zone whose reaction roll missed is noticed this much later. */
export const ZONE_MISS_MS = 2500;

/** Dangers this bot has noticed and decided to respect. */
export function respectedDangers(p: Perception, mem: BotMemory): DangerArea[] {
  const soakOk = safeSoakIds(p, mem);
  const out = p.dangers.filter((d) => {
    // +encounters: my assigned soak (or one I'm not eligible for) is not a danger.
    if (d.kind === 'telegraph' && soakOk.size > 0 && soakOk.has(d.key.slice(2))) return false;
    // Skill dial: per-stimulus reaction delay; a missed telegraph is never dodged, a missed zone
    // is only noticed ZONE_MISS_MS later (standing in a pool until it hurts).
    const rx = reactionRoll(mem, `dodge:${d.key}`);
    const react = rx.at + (rx.ok || d.kind !== 'zone' ? 0 : ZONE_MISS_MS);
    if (p.now < d.seenAt + react) return false;
    if (!rx.ok && d.kind !== 'zone') return false;
    if (d.kind === 'zone') {
      // Once noticed, a zone stays respected; until then roll again each window.
      if (mem.rolls.get(`dodge:${d.key}`)?.ok) return true;
      const bucket = Math.floor((p.now - d.seenAt - react) / Math.max(ZONE_REROLL_MS, mem.skill.reactionMs));
      const r = rollFor(mem, `dodge:${d.key}:${bucket}`, mem.skill.dodgeReliability);
      if (r.ok) mem.rolls.set(`dodge:${d.key}`, r);
      return r.ok;
    }
    return rollFor(mem, `dodge:${d.key}`, mem.skill.dodgeReliability).ok;
  });
  return withLookahead(p, out);
}

/** +encounters: add where each moving zone will be shortly (bouncing axes, spirals, chasers, turning beams). */
function withLookahead(p: Perception, dangers: DangerArea[]): DangerArea[] {
  let extra: DangerArea[] | null = null;
  for (const d of dangers) {
    if (d.kind !== 'zone') continue;
    const z = p.world.zones.find((zz) => `z:${zz.id}` === d.key);
    const m = z?.motion;
    if (!m) continue;
    const k = MOVING_LOOKAHEAD_MS / 1000;
    if (m.spec.type === 'aim') {
      // Tracking beam: also avoid where it will have turned to (toward its target, capped turn rate).
      // The beam's own target kites it (mechanics.ts kiteDuty); this is for everyone else.
      const t = m.targetId && m.targetId !== p.self.id ? p.world.units[m.targetId] : undefined;
      if (!t) continue;
      const want = Math.atan2(t.pos.y - d.area.origin.y, t.pos.x - d.area.origin.x);
      const diff = angleDiff(want, d.area.facing);
      const turn = Math.sign(diff) * Math.min(Math.abs(diff), m.spec.turnRate * k);
      if (Math.abs(turn) < 1e-3) continue;
      (extra ??= []).push({ ...d, key: `${d.key}:ahead`, area: { ...d.area, facing: d.area.facing + turn } });
      continue;
    }
    const v = m.vel;
    if (!v || (v.x === 0 && v.y === 0)) continue;
    (extra ??= []).push({ ...d, key: `${d.key}:ahead`, area: { ...d.area, origin: { x: d.area.origin.x + v.x * k, y: d.area.origin.y + v.y * k } } });
  }
  return extra ? [...dangers, ...extra] : dangers;
}

/** Does any of these areas cover the point (local geometry OR the sim's own test). */
export function inDanger(p: Perception, dangers: readonly DangerArea[], at: Vec2, radius: number): boolean {
  return dangers.some((d) => covers(p, d.area, at, radius));
}

function covers(p: Perception, area: DeepReadonly<WorldShape>, at: Vec2, radius: number): boolean {
  return shapeContains(area, at, radius) || p.view.queries.spatial.shapeContains(area as WorldShape, at, radius);
}

/** Safe point near `from`, biased toward `prefer`. */
export function safePoint(p: Perception, dangers: readonly DangerArea[], from: Vec2, prefer?: Vec2): Vec2 | null {
  const r = p.self.radius;
  const fromSim = p.view.queries.spatial.nearestSafePoint(simWorld(p), from, dangers.map((d) => d.area as WorldShape), r);
  if (fromSim && !inDanger(p, dangers, fromSim, r)) return fromSim;
  return findSafePoint(
    from,
    dangers.map((d) => d.area),
    r,
    (c) => p.view.queries.spatial.isWalkable(simWorld(p), c) && !inDanger(p, dangers, c, r + 0.75),
    prefer,
  );
}

/** Move commands if I'm standing in something I noticed; null when safe. */
export function planDodge(p: Perception, mem: BotMemory, prefer?: Vec2): Command[] | null {
  const dangers = respectedDangers(p, mem);
  if (dangers.length === 0 || !inDanger(p, dangers, p.self.pos, p.self.radius)) return null;
  // Already running to a point that is still safe: keep going.
  const last = mem.lastMoveDest;
  if (last && p.self.move && !inDanger(p, dangers, last, p.self.radius + 0.5)) return [];
  const dest = safePoint(p, dangers, p.self.pos, prefer);
  if (!dest) return null;
  const cmds: Command[] = [];
  if (p.self.cast) {
    const ab = p.view.content.ability(p.self.cast.abilityId);
    if (!ab?.canCastWhileMoving) cmds.push({ type: 'cancelCast', actorId: p.self.id });
  }
  cmds.push({ type: 'move', actorId: p.self.id, dest });
  mem.lastMoveDest = dest;
  return cmds;
}
